<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { IAppointment, LoadTypeDirection, LoadTypeOperation } from '@nova/core';

import {
  getLoadTypeDirectionArrowIcon,
  getLoadTypeDirectionI18nKey,
  getLoadTypeOperationI18nKey
} from '@/utils/assetVisitType';

defineOptions({ name: 'AppointmentLoadTypeDetails' });

const { appointment, variant = 'neutral' } = defineProps<{
  appointment: IAppointment;
  variant?: 'highlighted' | 'neutral';
}>();

const { t } = useI18n();

const loadType = computed(() => appointment.loadType);

// The dock is only meaningful to the driver when the load type let the carrier pick it; otherwise
// it is an internal assignment that can still change before arrival.
const dock = computed(() =>
  loadType.value?.settings?.allowCarrierDockSelection === true ? appointment.dock : null
);

function getDirectionLabel(direction: LoadTypeDirection): string {
  const key = getLoadTypeDirectionI18nKey(direction);
  return key ? t(`initial.direction.${key}`) : direction;
}

function getOperationLabel(operation: LoadTypeOperation): string {
  const key = getLoadTypeOperationI18nKey(operation);
  return key ? t(`initial.operation.${key}`) : operation;
}
</script>

<template>
  <v-card
    v-if="loadType"
    variant="outlined"
    class="comet-i18n-exclude pa-4 d-flex flex-column gap-3"
    :class="
      variant === 'highlighted'
        ? 'load-type-details--highlighted'
        : 'bg--color-neutral-10 border--color-neutral-40'
    "
    data-testid="comet-appointment-details">
    <div class="d-flex gap-1">
      <span class="typography-body-md-bold">{{ t('initial.loadTypeLabel') }}:</span>
      <span class="typography-body-md">{{ loadType.name }}</span>
    </div>
    <div class="d-flex align-center gap-1">
      <span class="typography-body-md-bold">{{ t('initial.directionLabel') }}:</span>
      <span class="typography-body-md">{{ getDirectionLabel(loadType.direction) }}</span>
      <div class="d-flex align-center rounded-sm bg--color-primary-20 pa-1">
        <v-icon size="14" :icon="getLoadTypeDirectionArrowIcon(loadType.direction)"></v-icon>
        <v-icon size="14" icon="mdi-truck-outline"></v-icon>
      </div>
    </div>
    <div v-if="loadType.operation" class="d-flex gap-1">
      <span class="typography-body-md-bold">{{ t('initial.operationLabel') }}:</span>
      <span class="typography-body-md">{{ getOperationLabel(loadType.operation) }}</span>
    </div>
    <div v-if="dock" class="d-flex gap-1">
      <span class="typography-body-md-bold">{{ t('initial.dockLabel') }}:</span>
      <span class="typography-body-md">{{ dock.name }}</span>
    </div>
  </v-card>
</template>

<style scoped lang="scss">
.load-type-details--highlighted {
  background-color: rgba($color-primary-20, 0.3) !important;
  border-color: $color-primary-20 !important;
}
</style>
